interface SelectedPassage {
  title: string
  content: string
  originalContent?: string
  edp_idx?: number
  reg_date?: string
  passage_type?: string
  passage_type_name?: string
  passage_kr?: string | null
  creturn?: 'Y' | 'N'
  creturn_name?: string
}

// 지문 선택 결과 (추가 + 제거)
interface PassageSelectionResult {
  passages: Array<SelectedPassage> // 추가할 지문 목록
  removedPassageIds: number[] // 제거된 지문의 edp_idx 목록
}

interface DataLayerPopupState {
  savedPassageLoad: {
    isVisible: boolean
    data?: any
    callback?: (result: PassageSelectionResult) => void
  }
  folderDetail: {
    isVisible: boolean
    folderName: string
    folderId?: number
    passageCount?: number
    data?: any
  }
  newPassageInput: {
    isVisible: boolean
    initialData?: any
    callback?: (passage: SelectedPassage) => void
    folderId?: string | number | null
  }
  dataPreview: {
    isVisible: boolean
    data?: any
  }
}

// GA 이벤트 추적을 위한 Analytics 컴포저블
let analyticsComposable: any = null
let appStore: any = null

// 클라이언트 사이드에서만 Analytics 초기화
const initializeAnalytics = () => {
  if (process.client && !analyticsComposable) {
    try {
      analyticsComposable = useAnalytics()
      appStore = useAppStore()
    } catch (error) {
      console.warn('Analytics initialization failed:', error)
    }
  }
}

// GA 이벤트 추적 헬퍼 함수
const trackPopupEvent = (eventName: string, eventData: any) => {
  try {
    if (analyticsComposable?.trackEvent && appStore?.user) {
      analyticsComposable.trackEvent(eventName, {
        ...eventData,
        um_idx: appStore.user.UM_IDX ?? '',
        timestamp: new Date().toISOString()
      })
    }
  } catch (error) {
    console.warn('GA popup event tracking failed:', error)
  }
}

export const useDataLayerPopup = () => {
  const state = useState<DataLayerPopupState>('dataLayerPopup', () => ({
    savedPassageLoad: {
      isVisible: false,
      data: null,
    },
    folderDetail: {
      isVisible: false,
      folderName: '',
      folderId: undefined,
      passageCount: 0,
      data: null,
    },
    newPassageInput: {
      isVisible: false,
      initialData: null,
      folderId: null,
    },
    dataPreview: {
      isVisible: false,
      data: null,
    },
  }))

  const preventScroll = () => {
    if (process.client) {
      document.body.style.overflow = 'hidden'
    }
  }

  const allowScroll = () => {
    if (process.client) {
      document.body.style.overflow = ''
    }
  }

  const showSavedPassageLoad = (
    callback?: (result: PassageSelectionResult) => void,
    data?: any,
    selectedPassages?: Array<SelectedPassage>,
  ) => {
    initializeAnalytics()
    
    state.value.savedPassageLoad = {
      isVisible: true,
      data: { ...data, selectedPassages },
      callback,
    }
    
    // GA 이벤트: 저장된 지문 로드 팝업 열기
    trackPopupEvent('saved_passage_popup_opened', {
      event_category: 'popup_interaction',
      popup_type: 'saved_passage_load',
      has_callback: !!callback,
      selected_passages_count: selectedPassages?.length || 0,
      has_initial_data: !!data
    })
    
    preventScroll()
  }

  const hideSavedPassageLoad = () => {
    const previousData = state.value.savedPassageLoad.data
    
    state.value.savedPassageLoad.isVisible = false
    
    // GA 이벤트: 저장된 지문 로드 팝업 닫기
    trackPopupEvent('saved_passage_popup_closed', {
      event_category: 'popup_interaction',
      popup_type: 'saved_passage_load',
      had_selections: (previousData?.selectedPassages?.length || 0) > 0,
      close_method: 'manual'
    })
    
    if (!isAnyVisible.value) {
      allowScroll()
    }
  }

  const showFolderDetail = (folderName: string, data?: any) => {
    initializeAnalytics()
    
    state.value.folderDetail = {
      isVisible: true,
      folderName,
      folderId: data?.folderId,
      passageCount: data?.passageCount || 0,
      data,
    }
    
    // GA 이벤트: 폴더 상세 팝업 열기
    trackPopupEvent('folder_detail_popup_opened', {
      event_category: 'popup_interaction',
      popup_type: 'folder_detail',
      folder_name: folderName,
      folder_id: data?.folderId || null,
      passage_count: data?.passageCount || 0
    })
    
    preventScroll()
  }

  const hideFolderDetail = () => {
    const previousData = state.value.folderDetail
    
    state.value.folderDetail.isVisible = false
    
    // GA 이벤트: 폴더 상세 팝업 닫기
    trackPopupEvent('folder_detail_popup_closed', {
      event_category: 'popup_interaction',
      popup_type: 'folder_detail',
      folder_name: previousData.folderName,
      folder_id: previousData.folderId || null,
      passage_count: previousData.passageCount || 0,
      close_method: 'manual'
    })
    
    if (!isAnyVisible.value) {
      allowScroll()
    }
  }

  const showNewPassageInput = (
    callback?: (passage: SelectedPassage) => void,
    initialData?: any,
    folderId?: any,
  ) => {
    initializeAnalytics()
    
    state.value.newPassageInput = {
      isVisible: true,
      initialData,
      callback,
      folderId,
    }
    
    // GA 이벤트: 새 지문 입력 팝업 열기
    trackPopupEvent('new_passage_popup_opened', {
      event_category: 'popup_interaction',
      popup_type: 'new_passage_input',
      has_callback: !!callback,
      has_initial_data: !!initialData,
      target_folder_id: folderId || null
    })
    
    preventScroll()
  }

  const hideNewPassageInput = () => {
    const previousFolderId = state.value.newPassageInput.folderId
    
    state.value.newPassageInput.isVisible = false
    state.value.newPassageInput.folderId = ''
    
    // GA 이벤트: 새 지문 입력 팝업 닫기
    trackPopupEvent('new_passage_popup_closed', {
      event_category: 'popup_interaction',
      popup_type: 'new_passage_input',
      had_folder_id: !!previousFolderId,
      close_method: 'manual'
    })
    
    if (!isAnyVisible.value) {
      allowScroll()
    }
  }

  const showDataPreview = (data?: any) => {
    initializeAnalytics()
    
    state.value.dataPreview = {
      isVisible: true,
      data,
    }
    
    // GA 이벤트: 데이터 미리보기 팝업 열기
    trackPopupEvent('data_preview_popup_opened', {
      event_category: 'popup_interaction',
      popup_type: 'data_preview',
      item_id: data?.sub_id || 'unknown',
      item_name: data?.sub_name || 'Unknown',
      item_type: data?.passage_type || 'unknown',
      has_preview_data: !!data
    })
    
    preventScroll()
  }

  const hideDataPreview = () => {
    const previousData = state.value.dataPreview.data
    
    state.value.dataPreview.isVisible = false
    
    // GA 이벤트: 데이터 미리보기 팝업 닫기
    trackPopupEvent('data_preview_popup_closed', {
      event_category: 'popup_interaction',
      popup_type: 'data_preview',
      item_id: previousData?.sub_id || 'unknown',
      item_name: previousData?.sub_name || 'Unknown',
      close_method: 'manual' // 수동 닫기
    })
    
    if (!isAnyVisible.value) {
      allowScroll()
    }
  }

  const closeAll = () => {
    state.value.savedPassageLoad.isVisible = false
    state.value.folderDetail.isVisible = false
    state.value.newPassageInput.isVisible = false
    state.value.dataPreview.isVisible = false
    allowScroll()
  }

  const isAnyVisible = computed(() => {
    return (
      state.value.savedPassageLoad.isVisible ||
      state.value.folderDetail.isVisible ||
      state.value.newPassageInput.isVisible ||
      state.value.dataPreview.isVisible
    )
  })

  return {
    state: readonly(state),

    // Saved Passage Load
    showSavedPassageLoad,
    hideSavedPassageLoad,

    // Folder Detail
    showFolderDetail,
    hideFolderDetail,

    // New Passage Input
    showNewPassageInput,
    hideNewPassageInput,

    // Data Preview
    showDataPreview,
    hideDataPreview,

    // Utility
    closeAll,
    isAnyVisible,
  }
}
